import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Dialog, DialogTitle, Grid, Typography } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useHoverNavigation from '../use-hover-navigation';
import { useHoverNavigationContext } from '../hover-navigation-context';
import { hoverHighlightSx } from '../hover-style';
import { useGridData } from './grid-data-context';
import EditDialog from './edit-dialog';
import AppsActionBarWithSettings from '../apps-action-bar-with-settings';

const cellSx = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    userSelect: 'none',
    transition: 'box-shadow 0.3s ease, filter 0.3s ease',
    borderRadius: 4,
    border: '4px solid',
    borderColor: 'primary.main',
    p: 3,
    minHeight: 120,
};


export default function GridApp() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { active: navigationActive, toggle: toggleNavigation } = useHoverNavigationContext();
    const { nodes: rootNodes, setNodes: setRootNodes, saveConfiguration, loading } = useGridData();
    const [navPath, setNavPath] = useState([]); // array of indices into the tree
    const [popupLabel, setPopupLabel] = useState(null);
    const [editOpen, setEditOpen] = useState(false);
    const [saving, setSaving] = useState(false);
    const [actionBarActions, setActionBarActions] = useState([]);

    const currentNodes = useMemo(() => {
        let nodes = rootNodes;
        for (const idx of navPath) {
            nodes = nodes[idx].children;
        }
        return nodes;
    }, [rootNodes, navPath]);

    // "Back to Level" shown as first grid cell when navigated deeper
    const hasBackCell = navPath.length > 0;

    const handleBackToLevel = useCallback(() => {
        setNavPath(prev => prev.slice(0, -1));
    }, []);

    // Helper actions (right side, NOT accessible via hover navigation)
    const helperActions = useMemo(() => [{
        icon: EditIcon,
        label: t('APPLICATIONS.GRID.EDIT_TITLE'),
        onClick: () => setEditOpen(true),
    }], [t]);

    const gridItemCount = (hasBackCell ? 1 : 0) + currentNodes.length;
    const itemCount = gridItemCount + actionBarActions.length;

    const onSelect = useCallback((index) => {
        // Back cell is at index 0 when present
        if (hasBackCell && index === 0) {
            handleBackToLevel();
            return;
        }
        const nodeIndex = hasBackCell ? index - 1 : index;
        if (nodeIndex < currentNodes.length) {
            // Grid node clicked
            const node = currentNodes[nodeIndex];
            if (node.children?.length > 0) {
                setNavPath(prev => [...prev, nodeIndex]);
            } else {
                // Leaf node selected - show popup and turn off navigation
                setPopupLabel(node.label);
                if (navigationActive) {
                    toggleNavigation();
                }
            }
        } else {
            // Action bar item clicked
            const actionIndex = index - gridItemCount;
            if (actionIndex >= 0 && actionIndex < actionBarActions.length) {
                actionBarActions[actionIndex].onClick();
            }
        }
    }, [currentNodes, actionBarActions, navigationActive, toggleNavigation, hasBackCell, handleBackToLevel, gridItemCount]);

    const { active, activeIndex } = useHoverNavigation(itemCount, onSelect);

    // Close popup when Enter is pressed (prevent navigation toggle while popup is open)
    useEffect(() => {
        if (popupLabel === null) return;

        const handleKeyDown = (e) => {
            if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
                setPopupLabel(null);
            }
        };

        window.addEventListener('keydown', handleKeyDown, true); // Use capture phase to intercept before hover navigation
        return () => window.removeEventListener('keydown', handleKeyDown, true);
    }, [popupLabel]);

    const handleSaveTree = useCallback(async (newTree) => {
        setSaving(true);
        const success = await saveConfiguration(newTree);
        setSaving(false);

        if (success) {
            setNavPath([]);
            setEditOpen(false);
        } else {
            // Could show an error snackbar here
            alert(t('APPLICATIONS.GRID.SAVE_ERROR') || 'Failed to save configuration');
        }
    }, [saveConfiguration, t]);

    const totalCells = (hasBackCell ? 1 : 0) + currentNodes.length;
    const cols = totalCells <= 2 ? 6 : totalCells <= 4 ? 6 : 4;

    // Show loading state
    if (loading) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', width: '100%' }}>
                <Typography variant="h5">{t('APPLICATIONS.GRID.LOADING') || 'Loading...'}</Typography>
            </Box>
        );
    }

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', width: '100%' }}>
            {/* Action bar */}
            <AppsActionBarWithSettings
                helperActions={helperActions}
                activeIndex={activeIndex - gridItemCount}
                active={active && activeIndex >= gridItemCount}
                onActionsChange={setActionBarActions}
                flex="0 0 auto"
            />

            {/* Grid area — 80% */}
            <Box sx={{ flex: '1 1 80%', display: 'flex', alignItems: 'center', justifyContent: 'center', p: 2, minHeight: 0 }}>
                <Grid container spacing={3} sx={{ maxWidth: 900 }} justifyContent="center">
                    {hasBackCell && (
                        <Grid item xs={cols} key="back">
                            <Box
                                onClick={handleBackToLevel}
                                sx={{
                                    ...cellSx,
                                    ...(active && activeIndex === 0 && hoverHighlightSx),
                                }}
                            >
                                <ArrowBackIcon sx={{ fontSize: 48 }} />
                                <Typography variant="h4" sx={{ fontWeight: 700, textAlign: 'center' }}>
                                    {t('APPLICATIONS.BACK_TO_LEVEL')}
                                </Typography>
                            </Box>
                        </Grid>
                    )}
                    {currentNodes.map((node, index) => {
                        const itemIndex = hasBackCell ? index + 1 : index;
                        return (
                            <Grid item xs={cols} key={node.id}>
                                <Box
                                    onClick={() => onSelect(itemIndex)}
                                    sx={{
                                        ...cellSx,
                                        ...(active && activeIndex === itemIndex && hoverHighlightSx),
                                    }}
                                >
                                    <Typography variant="h4" sx={{ fontWeight: 700, textAlign: 'center' }}>
                                        {node.label}
                                    </Typography>
                                </Box>
                            </Grid>
                        );
                    })}
                </Grid>
            </Box>

            {/* Word popup */}
            <Dialog open={popupLabel !== null} onClose={() => setPopupLabel(null)}>
                <DialogTitle sx={{ textAlign: 'center', px: 8, py: 4, fontSize: '2rem' }}>
                    {popupLabel}
                </DialogTitle>
            </Dialog>

            <EditDialog
                open={editOpen}
                onClose={() => !saving && setEditOpen(false)}
                tree={rootNodes}
                onSave={handleSaveTree}
                saving={saving}
            />
        </Box>
    );
}
